/**
 * ContestPromoSlotOverlay — image-slot promo overlay (2026-07-21 dispatch).
 *
 * Renders ONLY when:
 *   * A contest round is active (via `useActiveContest`).
 *   * The parent slot has NO winner photo assigned (parent passes
 *     `hasWinnerPhoto={false}` — the parent knows its own state).
 *
 * The parent wraps this in a relative-positioned container that
 * already renders the underlying image. This component adds a
 * dark-gradient scrim + top-right purple "CONTEST" LABEL pill +
 * bottom-center "Your dog could be here" text + orange CTA that
 * routes to `/cutest-pluck`.
 *
 * Role-based color tokens (Item 1 theme-drift sweep, 2026-07-23):
 *   * LABEL (state announcement, non-interactive `<span>` chip):
 *     `bg-pp-purple` + `text-white`. Purple matches the eyebrow/
 *     label role used elsewhere in the design system and separates
 *     the announcement visually from the CTA underneath. WCAG AA
 *     white-on-#574B8C ≈ 7.65:1.
 *   * ACTION (pressable `<Link>` CTA): `bg-pp-orange` +
 *     `text-pp-cta-text`. Both tokens are theme-invariant so the
 *     button contrast holds in both light and dark modes. The old
 *     `text-pp-slate` foreground flipped to near-white
 *     (`#F9FAFB`) in dark mode per `index.css:483`, producing a
 *     WHITE-on-orange invisible button — same class of bug as the
 *     ContestPromoTopBar's Enter CTA. WCAG AA #2B3942-on-#F89623 ≈
 *     5.21:1. See `src/meta.theme-drift-guard.test.js`.
 *
 * Gradient uses Tailwind's `from-black/70` opacity token (not raw
 * hex). White text on the black/60→black/0 gradient meets WCAG AA
 * for large text. The CTA is a real `<Link>`.
 *
 * Scope note (2026-07-21): the dispatch limits the overlay to
 * exactly TWO existing slots — landing hero + about hero.
 * Not-yet-existing slots 5–10 are out of scope.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useActiveContest } from "@/context/ActiveContestContext";

export default function ContestPromoSlotOverlay({
  hasWinnerPhoto = false,
  testIdSuffix = "",
}) {
  const { active, loading } = useActiveContest();
  if (loading || !active || hasWinnerPhoto) return null;

  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";
  return (
    <div
      className="pointer-events-none absolute inset-0 flex flex-col justify-end overflow-hidden rounded-[inherit]"
      data-testid={`contest-promo-slot-overlay${suffix}`}
      aria-hidden="false"
    >
      {/* Top-right small chip. */}
      <div className="pointer-events-none absolute right-3 top-3">
        <span
          className="inline-flex items-center gap-1 rounded-full bg-pp-purple px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm"
          data-testid={`contest-promo-slot-chip${suffix}`}
        >
          <span aria-hidden="true">🐾</span> Contest
        </span>
      </div>

      {/* Bottom gradient + text + CTA. */}
      <div className="pointer-events-auto bg-gradient-to-t from-black/75 via-black/45 to-transparent px-5 pb-5 pt-14 text-center">
        <p
          className="text-base font-semibold text-white sm:text-lg"
          data-testid={`contest-promo-slot-headline${suffix}`}
        >
          Your dog could be here
        </p>
        <Link
          to="/cutest-pluck"
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-pp-orange px-4 py-2 text-sm font-semibold text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid={`contest-promo-slot-cta${suffix}`}
          aria-label="Enter the Cutest Pluck contest"
        >
          Enter the contest
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
